import React from 'react'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import {
  usePrerenderReady,
  configureAuth,
  AuthProvider,
  useAuth,
  LoginForm,
  RegisterForm,
  ForgotPasswordForm,
  ResetPasswordForm,
  PublicRoute,
  ProtectedRoute,
  storage,
  ErrorBoundary,
  UpdateAvailableBanner,
  VERSION_ANNOUNCED_EVENT,
} from '@byscott-io/corebyscott'
import { usePageTracking, useMetricsIdentity } from '@byscott-io/metricsbyscott-web'
import { configureCable, PresenceProvider, useWaldoEvent } from '@byscott-io/where-is-waldo'

import UserStore from '../contexts/UserStore'
import PageWrapper from './Shared/PageWrapper'
import AppWrapper from './Shared/AppWrapper'
import MarketingContainer from './Marketing/Container'
import PrioritizePlusRoot from './PrioritizePlus/Root'
import RealtimeActivity from './PrioritizePlus/RealtimeActivity'
import { ReactNotifications } from 'react-notifications-component'
import Logout from './Shared/Logout'
import { CookiesProvider } from 'react-cookie'

// Corebyscott auth (F3, project #4). Auth runs on /api/auth/*; the domain API stays on /api/v1.
// Token is stored in cookies by corebyscott's storage util (cbs_auth_*).
configureAuth({
  appName: 'Prioritize.Plus',
  containerName: 'account',
  containerLabel: 'Account',
  containerLabelPlural: 'Accounts',
  containerHeader: 'X-Account-Id',
  containerBasePath: '/app',
  apiBasePath: '/api',
  jwtIncludeContainer: false,
  loginPath: '/login',
  registerPath: '/signup',
  forgotPasswordPath: '/forgot_password',
  resetPasswordPath: '/reset_password',
  dashboardPath: '/app',
  roles: ['owner', 'admin', 'member'],
  adminRoles: ['owner', 'admin'],
  features: {
    invitations: true,
    passwordReset: true,
    sessionsManagement: true,
    multiContainer: true,
    registration: true,
    mfa: true,
  },
})

// Realtime via whereiswaldo (#629) — the cable authenticates off the corebyscott
// JWT (same token the REST client sends), read from corebyscott's cookie storage.
configureCable({
  url: '/cable',
  getToken: () => storage.getToken(),
  presence: { debug: false },
})

function PageTracker() {
  const location = useLocation()
  usePageTracking(location.pathname)
  return null
}

function MetricsIdentity() {
  useMetricsIdentity(useAuth())
  return null
}

// TWO BOUNDARIES, doing different jobs. This inner one is inside the router and
// resets on navigation, so a page that throws is recovered by going elsewhere.
// The outer one sits OUTSIDE the providers, so it also catches a crash in a
// provider, which this one cannot -- it mounts underneath them. Without either,
// an uncaught render error unmounts the tree and leaves a blank page.
function RouteBoundary({ children }) {
  const location = useLocation()
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
}

// A deploy announces itself over waldo when the new container boots. Core must
// not import the waldo client -- not every app has one -- so the app turns the
// broadcast into the window event core listens for. The payload is not used: it
// only prompts a fresh ask of /api/version, which is what decides.
function VersionAnnouncements() {
  useWaldoEvent('app_version', () => {
    window.dispatchEvent(new Event(VERSION_ANNOUNCED_EVENT))
  })
  return null
}

const App = props => {
  usePrerenderReady()
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <UpdateAvailableBanner />
        <PageTracker />
        <MetricsIdentity />
        <CookiesProvider>
          <PresenceProvider>
            <VersionAnnouncements />
            <UserStore {...props}>
              <AppWrapper>
                <>
                  <ReactNotifications />
                  <RealtimeActivity />
                  <RouteBoundary>
                  <Routes>
                    <Route path="/signup" element={<PublicRoute><div className="cbs-scope"><RegisterForm /></div></PublicRoute>} />
                    <Route path="/login" element={<PublicRoute><div className="cbs-scope"><LoginForm /></div></PublicRoute>} />
                    <Route path="/forgot_password" element={<PublicRoute><div className="cbs-scope"><ForgotPasswordForm /></div></PublicRoute>} />
                    <Route path="/reset_password" element={<PublicRoute><div className="cbs-scope"><ResetPasswordForm /></div></PublicRoute>} />
                    <Route path="/logout" element={<PageWrapper name="Logout"><Logout /></PageWrapper>} />
                    <Route path="/app/*" element={<ProtectedRoute><PrioritizePlusRoot /></ProtectedRoute>} />
                    <Route path="*" element={<MarketingContainer />} />
                  </Routes>
                  </RouteBoundary>
                </>
              </AppWrapper>
            </UserStore>
          </PresenceProvider>
        </CookiesProvider>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
